/**
 * Grid Templates — predefined layout configurations for each style.
 *
 * Phase 0: 3 hard templates (Classic Standard, Premium Plus, Style4).
 * Phase 1: remaining 28 templates.
 *
 * Each template is a GridLayoutConfig that maps the existing JSX layout's
 * section placement into explicit rows and cells.
 *
 * IMPORTANT: All 3 classic5050 layouts are currently full-width stacked
 * (every section is col-span-full). Internal multi-column grids happen
 * WITHIN each section's render function, not at the grid level.
 * The grid engine will enable true 2D placement once templates evolve.
 */

import { createGridLayout, createRow, createCell } from './gridSchema'

// ---------------------------------------------------------------------------
// Section classification for automatic grid redistribution.
// When switching to a multi-column grid layout, sections from the current
// layout style get classified and placed into the appropriate grid zones.
// ---------------------------------------------------------------------------

const HERO_SECTIONS = new Set([
  'image_slider', 'hero_section', 'hero_carousel', 'charity_hero',
])

// Sections that must always span full width (never squeezed into a column)
const FULL_WIDTH_SECTIONS = new Set([
  'organization_writeup',
  'organization_writeup_2',
  'square_grid',
])

const SIDEBAR_SECTIONS = new Set([
  'quick_stats', 'charity_sidebar', 'charity_impact', 'charity_strip',
  'charity_banner', 'partner_banner', 'top_partners', 'leaderboard',
  'how_it_works', 'about_section', 'about_text', 'info_callouts',
  'stats_winners', 'final_cta',
])

const FOOTER_SECTIONS = new Set([
  'spacer_1', 'spacer_2', 'spacer_3',
  'content_block_1', 'content_block_2', 'content_block_3',
])

/**
 * Map section width config values to grid colSpan.
 * Supports numeric column spans (1-5), 'full', and legacy named widths.
 * @param {string|number} width — 'full', 1-5, or legacy 'two-thirds'/'half'/'third'
 * @param {number} totalCols — grid column count (2-5)
 * @returns {number} colSpan value
 */
export function widthToColSpan(width, totalCols) {
  if (width === 'full') return totalCols;
  // Numeric column span
  const n = Number(width);
  if (!isNaN(n) && n >= 1) return Math.max(1, Math.min(n, totalCols));
  // Legacy named widths (backward compat)
  switch (width) {
    case 'two-thirds': return Math.max(1, Math.ceil(totalCols * 2 / 3))
    case 'half':       return Math.max(1, Math.ceil(totalCols / 2))
    case 'third':      return Math.max(1, Math.ceil(totalCols / 3))
    default:           return totalCols
  }
}

/**
 * Map existing section height config values to grid rowSpan.
 * @param {number} height — 1 or 2
 * @returns {number} rowSpan value
 */
export function heightToRowSpan(height) {
  return Number(height) === 2 ? 2 : 1
}

/**
 * Redistribute sections from a source template into a multi-column grid pattern.
 * Preserves ALL sections from the source — nothing is lost.
 *
 * @param {Object} sourceTemplate — stacked GridLayoutConfig (the current layout style's template)
 * @param {'stack'|'hero-sidebar'|'magazine'|'dashboard'|'mosaic'} pattern — target grid pattern
 * @param {Object} [sectionConfigs={}] — optional per-section config overrides (width, height, stretch)
 * @returns {Object} new GridLayoutConfig with sections redistributed
 */
export function redistributeSections(sourceTemplate, pattern, sectionConfigs = {}) {
  if (!sourceTemplate || !sourceTemplate.rows) return sourceTemplate

  // Extract all sections from the source template (preserving variants and config)
  const allSections = sourceTemplate.rows
    .flatMap(row => row.cells)
    .filter(cell => cell.sectionType && !cell.hidden)
    .map(cell => ({
      type: cell.sectionType,
      variant: cell.variant || 'default',
      config: { ...cell.config, ...(sectionConfigs[cell.sectionType] || {}) },
    }))

  // Classify sections
  const heroes = allSections.filter(s => HERO_SECTIONS.has(s.type))
  const fullWidth = allSections.filter(s => FULL_WIDTH_SECTIONS.has(s.type))
  const sidebars = allSections.filter(s => SIDEBAR_SECTIONS.has(s.type))
  const footers = allSections.filter(s => FOOTER_SECTIONS.has(s.type))
  const mainContent = allSections.filter(s =>
    !HERO_SECTIONS.has(s.type) &&
    !FULL_WIDTH_SECTIONS.has(s.type) &&
    !SIDEBAR_SECTIONS.has(s.type) &&
    !FOOTER_SECTIONS.has(s.type)
  )

  const id = `${sourceTemplate.id}-${pattern}`
  const name = `${sourceTemplate.name} (${pattern})`
  const overrides = {
    theme: sourceTemplate.theme || 'light',
    gameType: sourceTemplate.gameType,
  }

  const toEntry = s => s.variant !== 'default' ? { type: s.type, variant: s.variant } : s.type
  // Full-width sections are appended after heroes so they always span all columns
  const heroEntries = [...heroes, ...fullWidth].map(toEntry)

  switch (pattern) {
    case 'stack':
      return stackedTemplate(id, name,
        allSections.map(toEntry),
        overrides
      )

    case 'hero-sidebar':
      return heroSidebarTemplate(id, name,
        heroEntries,
        mainContent.map(toEntry),
        [...sidebars, ...footers].map(toEntry),
        overrides
      )

    case 'magazine':
      return magazineTemplate(id, name,
        heroEntries,
        [...mainContent, ...sidebars].map(toEntry),
        footers.map(toEntry),
        overrides
      )

    case 'dashboard':
      return fourColTemplate(id, name,
        heroEntries,
        mainContent.map(toEntry),
        sidebars.map(toEntry),
        footers.map(toEntry),
        overrides
      )

    case 'mosaic':
      return fiveColTemplate(id, name,
        heroEntries,
        mainContent.map(toEntry),
        sidebars.map(toEntry),
        footers.map(toEntry),
        overrides
      )

    default:
      return sourceTemplate
  }
}

// ---------------------------------------------------------------------------
// Helper: create a full-width stacked template (most common pattern)
// Uses deterministic IDs based on template ID and section type.
// ---------------------------------------------------------------------------

function stackedTemplate(id, name, sectionTypes, overrides = {}) {
  // Auto-append any FOOTER_SECTIONS not already listed
  const typeSet = new Set(sectionTypes.map(t => typeof t === 'string' ? t : t.type))
  const allTypes = [...sectionTypes]
  for (const ft of FOOTER_SECTIONS) {
    if (!typeSet.has(ft)) allTypes.push(ft)
  }

  return createGridLayout({
    id,
    name,
    type: 'template',
    columns: 1,
    rows: allTypes.map((type, idx) => {
      const sectionType = typeof type === 'string' ? type : type.type
      const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
      const hidden = typeof type === 'string' ? false : (type.hidden || false)
      return {
        id: `${id}-row-${idx}`,
        height: 'auto',
        minHeight: null,
        cells: [{
          id: `${id}-cell-${sectionType}`,
          colSpan: 1,
          rowSpan: 1,
          sectionType,
          variant,
          hidden,
          responsive: null,
          config: {},
        }],
      }
    }),
    ...overrides,
  })
}

// ---------------------------------------------------------------------------
// Helper: create a 2-column "Hero + Sidebar" template.
// Full-width hero on top, then main content (left) + sidebar (right).
// ---------------------------------------------------------------------------

function heroSidebarTemplate(id, name, heroSections, mainSections, sidebarSections, overrides = {}) {
  const rows = []

  // Hero row(s) — full-width (col-span 2)
  heroSections.forEach((type, idx) => {
    const sectionType = typeof type === 'string' ? type : type.type
    const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
    const hidden = typeof type === 'string' ? false : (type.hidden || false)
    rows.push({
      id: `${id}-hero-${idx}`,
      height: 'auto',
      minHeight: null,
      cells: [{
        id: `${id}-hero-cell-${sectionType}`,
        colSpan: 2,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      }],
    })
  })

  // Content rows — pair main (left) and sidebar (right)
  const maxRows = Math.max(mainSections.length, sidebarSections.length)
  for (let i = 0; i < maxRows; i++) {
    const cells = []
    
    // Left cell (main content)
    if (i < mainSections.length) {
      const type = mainSections[i]
      const sectionType = typeof type === 'string' ? type : type.type
      const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
      const hidden = typeof type === 'string' ? false : (type.hidden || false)
      cells.push({
        id: `${id}-main-cell-${sectionType}`,
        colSpan: 1,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      })
    }

    // Right cell (sidebar)
    if (i < sidebarSections.length) {
      const type = sidebarSections[i]
      const sectionType = typeof type === 'string' ? type : type.type
      const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
      const hidden = typeof type === 'string' ? false : (type.hidden || false)
      cells.push({
        id: `${id}-side-cell-${sectionType}`,
        colSpan: 1,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      })
    }

    rows.push({
      id: `${id}-content-${i}`,
      height: 'auto',
      minHeight: null,
      cells,
    })
  }

  return createGridLayout({
    id,
    name,
    type: 'template',
    columns: 2,
    rows,
    ...overrides,
  })
}

// ---------------------------------------------------------------------------
// Helper: create a 3-column "Magazine" template.
// Full-width hero, 3-column content grid, full-width footer sections.
// ---------------------------------------------------------------------------

function magazineTemplate(id, name, heroSections, triColSections, footerSections, overrides = {}) {
  const rows = []

  // Hero row(s) — full-width (col-span 3)
  heroSections.forEach((type, idx) => {
    const sectionType = typeof type === 'string' ? type : type.type
    const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
    const hidden = typeof type === 'string' ? false : (type.hidden || false)
    rows.push({
      id: `${id}-hero-${idx}`,
      height: 'auto',
      minHeight: null,
      cells: [{
        id: `${id}-hero-cell-${sectionType}`,
        colSpan: 3,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      }],
    })
  })

  // 3-column content — groups of 3
  for (let i = 0; i < triColSections.length; i += 3) {
    const cells = []
    for (let j = 0; j < 3 && (i + j) < triColSections.length; j++) {
      const type = triColSections[i + j]
      const sectionType = typeof type === 'string' ? type : type.type
      const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
      const hidden = typeof type === 'string' ? false : (type.hidden || false)
      cells.push({
        id: `${id}-col-cell-${sectionType}`,
        colSpan: 1,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      })
    }
    rows.push({
      id: `${id}-tricol-${i}`,
      height: 'auto',
      minHeight: null,
      cells,
    })
  }

  // Footer rows — full-width (col-span 3)
  footerSections.forEach((type, idx) => {
    const sectionType = typeof type === 'string' ? type : type.type
    const variant = typeof type === 'string' ? 'default' : (type.variant || 'default')
    const hidden = typeof type === 'string' ? false : (type.hidden || false)
    rows.push({
      id: `${id}-footer-${idx}`,
      height: 'auto',
      minHeight: null,
      cells: [{
        id: `${id}-footer-cell-${sectionType}`,
        colSpan: 3,
        rowSpan: 1,
        sectionType,
        variant,
        hidden,
        responsive: null,
        config: {},
      }],
    })
  })

  return createGridLayout({
    id,
    name,
    type: 'template',
    columns: 3,
    rows,
    ...overrides,
  })
}

// ---------------------------------------------------------------------------
// Helper: create a 4-column "Dashboard" template.
// Full-width hero, then 3-col main + 1-col sidebar, footer full-width.
// ---------------------------------------------------------------------------

function fourColTemplate(id, name, heroSections, mainSections, sidebarSections, footerSections, overrides = {}) {
  const rows = []
  const parseType = (type) => ({
    sectionType: typeof type === 'string' ? type : type.type,
    variant: typeof type === 'string' ? 'default' : (type.variant || 'default'),
    hidden: typeof type === 'string' ? false : (type.hidden || false),
  })

  // Hero rows — full-width (colSpan 4)
  heroSections.forEach((type, idx) => {
    const { sectionType, variant, hidden } = parseType(type)
    rows.push({
      id: `${id}-hero-${idx}`, height: 'auto', minHeight: null,
      cells: [{ id: `${id}-hero-cell-${sectionType}`, colSpan: 4, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} }],
    })
  })

  // Content rows — main (colSpan 3) + sidebar (colSpan 1)
  const maxRows = Math.max(mainSections.length, sidebarSections.length)
  for (let i = 0; i < maxRows; i++) {
    const cells = []
    if (i < mainSections.length) {
      const { sectionType, variant, hidden } = parseType(mainSections[i])
      cells.push({ id: `${id}-main-cell-${sectionType}`, colSpan: 3, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} })
    }
    if (i < sidebarSections.length) {
      const { sectionType, variant, hidden } = parseType(sidebarSections[i])
      cells.push({ id: `${id}-side-cell-${sectionType}`, colSpan: 1, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} })
    }
    rows.push({ id: `${id}-content-${i}`, height: 'auto', minHeight: null, cells })
  }

  // Footer rows — full-width (colSpan 4)
  footerSections.forEach((type, idx) => {
    const { sectionType, variant, hidden } = parseType(type)
    rows.push({
      id: `${id}-footer-${idx}`, height: 'auto', minHeight: null,
      cells: [{ id: `${id}-footer-cell-${sectionType}`, colSpan: 4, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} }],
    })
  })

  return createGridLayout({ id, name, type: 'template', columns: 4, rows, ...overrides })
}

// ---------------------------------------------------------------------------
// Helper: create a 5-column "Mosaic" template.
// Full-width hero, then mixed-width content cells across 5 columns, footer.
// Primary sections get colSpan 3 (wide), secondary get colSpan 2 (narrow).
// ---------------------------------------------------------------------------

function fiveColTemplate(id, name, heroSections, mainSections, sidebarSections, footerSections, overrides = {}) {
  const rows = []
  const parseType = (type) => ({
    sectionType: typeof type === 'string' ? type : type.type,
    variant: typeof type === 'string' ? 'default' : (type.variant || 'default'),
    hidden: typeof type === 'string' ? false : (type.hidden || false),
  })

  // Hero rows — full-width (colSpan 5)
  heroSections.forEach((type, idx) => {
    const { sectionType, variant, hidden } = parseType(type)
    rows.push({
      id: `${id}-hero-${idx}`, height: 'auto', minHeight: null,
      cells: [{ id: `${id}-hero-cell-${sectionType}`, colSpan: 5, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} }],
    })
  })

  // Interleave main (colSpan 3) + sidebar (colSpan 2) sections into rows
  // Alternate which side gets the wider span for visual variety
  const allContent = []
  let mi = 0, si = 0
  while (mi < mainSections.length || si < sidebarSections.length) {
    const cells = []
    if (mi < mainSections.length) {
      const { sectionType, variant, hidden } = parseType(mainSections[mi++])
      cells.push({ id: `${id}-wide-cell-${sectionType}`, colSpan: 3, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} })
    }
    if (si < sidebarSections.length) {
      const { sectionType, variant, hidden } = parseType(sidebarSections[si++])
      cells.push({ id: `${id}-narrow-cell-${sectionType}`, colSpan: 2, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} })
    }
    allContent.push({ id: `${id}-mosaic-${allContent.length}`, height: 'auto', minHeight: null, cells })
  }
  rows.push(...allContent)

  // Footer rows — full-width (colSpan 5)
  footerSections.forEach((type, idx) => {
    const { sectionType, variant, hidden } = parseType(type)
    rows.push({
      id: `${id}-footer-${idx}`, height: 'auto', minHeight: null,
      cells: [{ id: `${id}-footer-cell-${sectionType}`, colSpan: 5, rowSpan: 1, sectionType, variant, hidden, responsive: null, config: {} }],
    })
  })

  return createGridLayout({ id, name, type: 'template', columns: 5, rows, ...overrides })
}

// ---------------------------------------------------------------------------
// Classic 50/50 — Standard (2-col sidebar feel, but actually stacked)
// ---------------------------------------------------------------------------

export const classic5050Standard = stackedTemplate(
  'classic5050-standard',
  'Classic Standard',
  [
    'image_slider',
    'hero_section',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'last_draw_info',
    'how_it_works',
    'live_draw_card',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'charity_sidebar',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  {
    theme: 'light',
    gameType: 'classic5050',
  }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Premium Plus (most sections, gradient hero)
// ---------------------------------------------------------------------------

export const classic5050PremiumPlus = stackedTemplate(
  'classic5050-premium-plus',
  'Premium +',
  [
    'image_slider',
    { type: 'hero_carousel', variant: 'premium-plus' },
    'organization_writeup',
    'organization_writeup_2',
    'stats_winners',
    'how_it_works',
    'charity_impact',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'top_partners',
    'partner_banner',
    'final_cta',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  {
    theme: 'light',
    gameType: 'classic5050',
  }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Style 4 / Game Show (dark theme)
// ---------------------------------------------------------------------------

export const classic5050Style4 = stackedTemplate(
  'classic5050-style4',
  'Game Show',
  [
    'image_slider',
    { type: 'hero_section', variant: 'game-show' },
    'organization_writeup',
    'organization_writeup_2',
    { type: 'how_it_works', variant: 'game-show' },
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'charity_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  {
    theme: 'dark',
    gameType: 'classic5050',
  }
)

// ---------------------------------------------------------------------------
// Chase the Ace — Standard (from existing JSX)
// ---------------------------------------------------------------------------

export const chaseTheAceStyleStandard = stackedTemplate(
  'chase-standard',
  'Chase Standard',
  [
    'hero_section',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'chaseTheAce' }
)

// ---------------------------------------------------------------------------
// Square Game — Standard (from existing JSX)
// ---------------------------------------------------------------------------

export const squareGameStyleStandard = stackedTemplate(
  'square-standard',
  'Square Standard',
  [
    'hero_section',
    'charity_hero',
    'live_draw',
    'how_it_works',
    'square_grid',
    'compact_winners',
    'bonus_draws',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'squareGame' }
)

// ---------------------------------------------------------------------------
// Square Game — Charity Showcase (from existing JSX)
// ---------------------------------------------------------------------------

export const squareGameStyleCharity = stackedTemplate(
  'square-charity',
  'Charity Showcase',
  [
    'hero_section',
    'charity_hero',
    'live_draw',
    'how_it_works',
    'square_grid',
    'compact_winners',
    'bonus_draws',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'squareGame' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Style 1 (hero carousel)
// ---------------------------------------------------------------------------

export const classic5050Style1 = stackedTemplate(
  'classic5050-style1',
  'Style 1',
  [
    'image_slider',
    'hero_carousel',
    'organization_writeup',
    'organization_writeup_2',
    'charity_banner',
    'live_draw',
    'bonus_draws',
    'partner_banner',
    'charity_impact',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Style 2 (gradient hero with countdown)
// ---------------------------------------------------------------------------

export const classic5050Style2 = stackedTemplate(
  'classic5050-style2',
  'Style 2',
  [
    'image_slider',
    'hero_section',
    'organization_writeup',
    'organization_writeup_2',
    'charity_banner',
    'how_it_works',
    'bonus_draws',
    'partner_banner',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Style 3 (charity-focused with split display)
// ---------------------------------------------------------------------------

export const classic5050Style3 = stackedTemplate(
  'classic5050-style3',
  'Style 3',
  [
    'image_slider',
    'charity_banner',
    'hero_section',
    'organization_writeup',
    'organization_writeup_2',
    'how_it_works',
    'charity_impact',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Style 5 (Theater Marquee)
// ---------------------------------------------------------------------------

export const classic5050Style5 = stackedTemplate(
  'classic5050-style5',
  'Theater Marquee',
  [
    'image_slider',
    'hero_section',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'charity_impact',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Premium
// ---------------------------------------------------------------------------

export const classic5050Premium = stackedTemplate(
  'classic5050-premium',
  'Premium',
  [
    'image_slider',
    'hero_carousel',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'charity_impact',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

export const modified5050Premium = stackedTemplate(
  'modified5050-premium',
  'Premium',
  [
    'image_slider',
    'hero_carousel',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'charity_impact',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'modified5050' }
)

export const modified5050PremiumPlus = stackedTemplate(
  'modified5050-premium-plus',
  'Premium +',
  [
    'image_slider',
    { type: 'hero_carousel', variant: 'premium-plus' },
    'organization_writeup',
    'organization_writeup_2',
    'stats_winners',
    'how_it_works',
    'charity_impact',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'top_partners',
    'partner_banner',
    'final_cta',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'modified5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — V2 (minimalist/modern)
// ---------------------------------------------------------------------------

export const classic5050V2 = stackedTemplate(
  'classic5050-v2',
  'V2',
  [
    'image_slider',
    'hero_section',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'charity_impact',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Classic 50/50 — Fluid (single-column admin-sortable)
// ---------------------------------------------------------------------------

export const classic5050Fluid = stackedTemplate(
  'classic5050-fluid',
  'Fluid',
  [
    'image_slider',
    'hero_carousel',
    'organization_writeup',
    'organization_writeup_2',
    'live_draw',
    'bonus_draws',
    'charity_banner',
    'charity_info_card',
    'partner_banner',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'classic5050' }
)

// ---------------------------------------------------------------------------
// Progressive 50/50 — Standard
// ---------------------------------------------------------------------------

export const progressive5050Standard = stackedTemplate(
  'progressive-standard',
  'Progressive Standard',
  [
    'image_slider',
    'hero_section',
    'jackpot_card',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'charity_impact',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'progressive5050' }
)

// ---------------------------------------------------------------------------
// Progressive 50/50 — Style 2
// ---------------------------------------------------------------------------

export const progressive5050Style2 = stackedTemplate(
  'progressive-style2',
  'Progressive Style 2',
  [
    'image_slider',
    'hero_section',
    'jackpot_display',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'charity_banner',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'progressive5050' }
)

// ---------------------------------------------------------------------------
// Progressive 50/50 — Style 3
// ---------------------------------------------------------------------------

export const progressive5050Style3 = stackedTemplate(
  'progressive-style3',
  'Progressive Style 3',
  [
    'image_slider',
    'hero_section',
    'jackpot_card',
    'countdown',
    'live_draw',
    'bonus_draws',
    'how_it_works',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'progressive5050' }
)

// ---------------------------------------------------------------------------
// Chase the Ace — Style 1
// ---------------------------------------------------------------------------

export const chaseStyle1 = stackedTemplate(
  'chase-style1',
  'Chase Style 1',
  [
    'image_slider',
    'hero_section',
    'cards_remaining',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'rollover_timeline',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'chaseTheAce' }
)

// ---------------------------------------------------------------------------
// Chase the Ace — V2
// ---------------------------------------------------------------------------

export const chaseV2 = stackedTemplate(
  'chase-v2',
  'Chase V2',
  [
    'image_slider',
    'hero_section',
    'cards_remaining',
    'countdown',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'chaseTheAce' }
)

// ---------------------------------------------------------------------------
// Chase the Ace — Deck Runner
// ---------------------------------------------------------------------------

export const chaseDeckRunner = stackedTemplate(
  'chase-deckRunner',
  'Deck Runner',
  [
    'image_slider',
    'hero_section',
    'jackpot_summary',
    'cards_remaining',
    'deck_display',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'rollover_timeline',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'dark', gameType: 'chaseTheAce' }
)

// ---------------------------------------------------------------------------
// Elimination Draw — Standard
// ---------------------------------------------------------------------------

export const eliminationStandard = stackedTemplate(
  'elimination-standard',
  'Elimination Standard',
  [
    'image_slider',
    'hero_section',
    'elimination_tracker',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'recent_eliminations',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'eliminationDraw' }
)

// ---------------------------------------------------------------------------
// Elimination Draw — Style 1
// ---------------------------------------------------------------------------

export const eliminationStyle1 = stackedTemplate(
  'elimination-style1',
  'Elimination Style 1',
  [
    'image_slider',
    'hero_section',
    'countdown',
    'elimination_tracker',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'eliminationDraw' }
)

// ---------------------------------------------------------------------------
// Elimination Draw — Style 2
// ---------------------------------------------------------------------------

export const eliminationStyle2 = stackedTemplate(
  'elimination-style2',
  'Elimination Style 2',
  [
    'image_slider',
    'hero_section',
    'live_draw',
    'elimination_tracker',
    'recent_eliminations',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'eliminationDraw' }
)

// ---------------------------------------------------------------------------
// Prize Draw — Standard
// ---------------------------------------------------------------------------

export const prizeStandard = stackedTemplate(
  'prize-standard',
  'Prize Standard',
  [
    'image_slider',
    'hero_section',
    'prize_gallery',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'prizeDraw' }
)

// ---------------------------------------------------------------------------
// Prize Draw — Style 1
// ---------------------------------------------------------------------------

export const prizeStyle1 = stackedTemplate(
  'prize-style1',
  'Prize Style 1',
  [
    'image_slider',
    'hero_section',
    'prize_summary',
    'countdown',
    'prize_gallery',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'prizeDraw' }
)

// ---------------------------------------------------------------------------
// Prize Draw — Gallery Spotlight
// ---------------------------------------------------------------------------

export const prizeGallerySpotlight = stackedTemplate(
  'prize-gallerySpotlight',
  'Gallery Spotlight',
  [
    'image_slider',
    'hero_section',
    'prize_gallery',
    'prize_summary',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'prizeDraw' }
)

// ---------------------------------------------------------------------------
// Superball Bingo — Standard
// ---------------------------------------------------------------------------

export const superballStandard = stackedTemplate(
  'superball-standard',
  'Superball Standard',
  [
    'image_slider',
    'hero_section',
    'ball_tracker',
    'live_draw',
    'session_info',
    'how_it_works',
    'bonus_draws',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'superballBingo' }
)

// ---------------------------------------------------------------------------
// Superball Bingo — Style 1
// ---------------------------------------------------------------------------

export const superballStyle1 = stackedTemplate(
  'superball-style1',
  'Superball Style 1',
  [
    'image_slider',
    'hero_section',
    'countdown',
    'ball_tracker',
    'session_info',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'superballBingo' }
)

// ---------------------------------------------------------------------------
// Superball Bingo — Style 2
// ---------------------------------------------------------------------------

export const superballStyle2 = stackedTemplate(
  'superball-style2',
  'Superball Style 2',
  [
    'image_slider',
    'hero_section',
    'ball_tracker',
    'live_draw',
    'bonus_draws',
    'how_it_works',
    'recent_winners',
    'leaderboard',
    'compact_winners',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'superballBingo' }
)

// ---------------------------------------------------------------------------
// Superball Bingo — Style 3
// ---------------------------------------------------------------------------

export const superballStyle3 = stackedTemplate(
  'superball-style3',
  'Superball Style 3',
  [
    'image_slider',
    'hero_section',
    'session_info',
    'ball_tracker',
    'countdown',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'superballBingo' }
)

// ---------------------------------------------------------------------------
// Superball Bingo — Live Board
// ---------------------------------------------------------------------------

export const superballLiveBoard = stackedTemplate(
  'superball-liveBoard',
  'Live Board',
  [
    'image_slider',
    'hero_section',
    'ball_tracker',
    'live_board_display',
    'session_info',
    'bonus_draws',
    'recent_winners',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'superballBingo' }
)

// ---------------------------------------------------------------------------
// Square Game — Scoreboard
// ---------------------------------------------------------------------------

export const squareScoreboard = stackedTemplate(
  'square-gameday',
  'Scoreboard',
  [
    'image_slider',
    'hero_section',
    'square_grid',
    'live_draw',
    'how_it_works',
    'bonus_draws',
    'compact_winners',
    'leaderboard',
    'partner_banner',
    'spacer_1',
    'spacer_2',
    'spacer_3',
  ],
  { theme: 'light', gameType: 'squareGame' }
)

// ---------------------------------------------------------------------------
// Multi-column layout templates (shared across game types)
// These are game-agnostic — the sections they reference exist in all registries.
// ---------------------------------------------------------------------------

// Hero + Sidebar (2-column) — Classic 50/50
const classic5050HeroSidebar = heroSidebarTemplate(
  'classic5050-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section', 'organization_writeup', 'organization_writeup_2'],
  ['live_draw', 'live_draw_card', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'how_it_works', 'charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'classic5050' }
)

// Magazine (3-column) — Classic 50/50
const classic5050Magazine = magazineTemplate(
  'classic5050-magazine',
  'Magazine',
  ['image_slider', 'hero_section', 'organization_writeup', 'organization_writeup_2'],
  ['live_draw', 'quick_stats', 'bonus_draws', 'compact_winners', 'leaderboard', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'classic5050' }
)

// Hero + Sidebar (2-column) — Progressive 50/50
const progressive5050HeroSidebar = heroSidebarTemplate(
  'progressive5050-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['live_draw', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'how_it_works', 'charity_sidebar'],
  { theme: 'light', gameType: 'progressive5050' }
)

// Magazine (3-column) — Progressive 50/50
const progressive5050Magazine = magazineTemplate(
  'progressive5050-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['live_draw', 'quick_stats', 'bonus_draws', 'compact_winners', 'leaderboard', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'progressive5050' }
)

// Hero + Sidebar (2-column) — Chase the Ace
const chaseHeroSidebar = heroSidebarTemplate(
  'chase-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['live_draw', 'cards_remaining', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'how_it_works', 'charity_sidebar'],
  { theme: 'light', gameType: 'chaseTheAce' }
)

// Magazine (3-column) — Chase the Ace
const chaseMagazine = magazineTemplate(
  'chase-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['live_draw', 'cards_remaining', 'quick_stats', 'bonus_draws', 'compact_winners', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'chaseTheAce' }
)

// Hero + Sidebar — Elimination Draw
const eliminationHeroSidebar = heroSidebarTemplate(
  'elimination-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['live_draw', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'how_it_works', 'charity_sidebar'],
  { theme: 'light', gameType: 'eliminationDraw' }
)

// Magazine — Elimination Draw
const eliminationMagazine = magazineTemplate(
  'elimination-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['live_draw', 'quick_stats', 'bonus_draws', 'compact_winners', 'leaderboard', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'eliminationDraw' }
)

// Hero + Sidebar — Prize Draw
const prizeHeroSidebar = heroSidebarTemplate(
  'prize-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['live_draw', 'prize_gallery', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'prize_summary', 'how_it_works', 'charity_sidebar'],
  { theme: 'light', gameType: 'prizeDraw' }
)

// Magazine — Prize Draw
const prizeMagazine = magazineTemplate(
  'prize-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['live_draw', 'prize_gallery', 'quick_stats', 'bonus_draws', 'compact_winners', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'prizeDraw' }
)

// Hero + Sidebar — Superball Bingo
const superballHeroSidebar = heroSidebarTemplate(
  'superball-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['live_draw', 'ball_tracker', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'session_info', 'how_it_works', 'charity_sidebar'],
  { theme: 'light', gameType: 'superballBingo' }
)

// Magazine — Superball Bingo
const superballMagazine = magazineTemplate(
  'superball-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['live_draw', 'ball_tracker', 'quick_stats', 'session_info', 'compact_winners', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'superballBingo' }
)

// Hero + Sidebar — Square Game
const squareHeroSidebar = heroSidebarTemplate(
  'square-hero-sidebar',
  'Hero + Sidebar',
  ['image_slider', 'hero_section'],
  ['square_grid', 'live_draw', 'bonus_draws', 'compact_winners'],
  ['quick_stats', 'how_it_works', 'leaderboard', 'charity_sidebar'],
  { theme: 'light', gameType: 'squareGame' }
)

// Magazine — Square Game
const squareMagazine = magazineTemplate(
  'square-magazine',
  'Magazine',
  ['image_slider', 'hero_section'],
  ['square_grid', 'live_draw', 'quick_stats', 'bonus_draws', 'compact_winners', 'how_it_works'],
  ['charity_sidebar', 'partner_banner'],
  { theme: 'light', gameType: 'squareGame' }
)

// ---------------------------------------------------------------------------
// Template registry — maps layoutStyle ID → template config
// ---------------------------------------------------------------------------

const TEMPLATES = {
  // Classic 50/50
  'standard': classic5050Standard,
  'style1': classic5050Style1,
  'style2': classic5050Style2,
  'style3': classic5050Style3,
  'style4': classic5050Style4,
  'style5': classic5050Style5,
  'premium': classic5050Premium,
  'premium-plus': classic5050PremiumPlus,
  'v2': classic5050V2,
  'fluid': classic5050Fluid,

  // Multi-column — Classic 50/50
  'hero-sidebar': classic5050HeroSidebar,
  'magazine': classic5050Magazine,
  'stack': classic5050Standard, // alias: "Full-Width Stack" schematic → standard stacked template

  // Modified 50/50
  'modified-premium': modified5050Premium,
  'modified-premium-plus': modified5050PremiumPlus,

  // Progressive 50/50
  'progressive-standard': progressive5050Standard,
  'progressive-style2': progressive5050Style2,
  'progressive-style3': progressive5050Style3,
  'progressive-hero-sidebar': progressive5050HeroSidebar,
  'progressive-magazine': progressive5050Magazine,
  'progressive-stack': progressive5050Standard,

  // Chase the Ace
  'chase-standard': chaseTheAceStyleStandard,
  'chase-style1': chaseStyle1,
  'chase-v2': chaseV2,
  'chase-deckRunner': chaseDeckRunner,
  'chase-hero-sidebar': chaseHeroSidebar,
  'chase-magazine': chaseMagazine,
  'chase-stack': chaseTheAceStyleStandard,

  // Elimination Draw
  'elimination-standard': eliminationStandard,
  'elimination-style1': eliminationStyle1,
  'elimination-style2': eliminationStyle2,
  'elimination-hero-sidebar': eliminationHeroSidebar,
  'elimination-magazine': eliminationMagazine,
  'elimination-stack': eliminationStandard,

  // Prize Draw
  'prize-standard': prizeStandard,
  'prize-style1': prizeStyle1,
  'prize-gallerySpotlight': prizeGallerySpotlight,
  'prize-hero-sidebar': prizeHeroSidebar,
  'prize-magazine': prizeMagazine,
  'prize-stack': prizeStandard,

  // Superball Bingo
  'superball-standard': superballStandard,
  'superball-style1': superballStyle1,
  'superball-style2': superballStyle2,
  'superball-style3': superballStyle3,
  'superball-liveBoard': superballLiveBoard,
  'superball-hero-sidebar': superballHeroSidebar,
  'superball-magazine': superballMagazine,
  'superball-stack': superballStandard,

  // Square Game
  'square-standard': squareGameStyleStandard,
  'square-gameday': squareScoreboard,
  'square-charity': squareGameStyleCharity,
  'square-hero-sidebar': squareHeroSidebar,
  'square-magazine': squareMagazine,
  'square-stack': squareGameStyleStandard,
}

// Game type → template key prefix mapping.
// Classic 50/50 uses direct keys (no prefix); all others are prefixed.
const GAME_TYPE_PREFIXES = {
  modified5050: 'modified',
  progressive5050: 'progressive',
  chaseTheAce: 'chase',
  eliminationDraw: 'elimination',
  prizeDraw: 'prize',
  superballBingo: 'superball',
  squareGame: 'square',
}

/**
 * Resolve a template by layoutStyle ID, optionally scoped to a game type.
 * For non-classic games, tries the game-prefixed key first (e.g. 'chase-standard'),
 * then falls back to the direct key.
 * Returns the GridLayoutConfig or null if no template exists.
 */
export function getTemplate(layoutStyle, gameType) {
  if (gameType) {
    const prefix = GAME_TYPE_PREFIXES[gameType]
    if (prefix) {
      const prefixedKey = `${prefix}-${layoutStyle}`
      if (TEMPLATES[prefixedKey]) return TEMPLATES[prefixedKey]
    }
  }
  return TEMPLATES[layoutStyle] || null
}

/**
 * Get all available templates.
 */
export function getAllTemplates() {
  return { ...TEMPLATES }
}

/**
 * Get templates filtered by game type.
 */
export function getTemplatesForGameType(gameType) {
  return Object.fromEntries(
    Object.entries(TEMPLATES).filter(([, t]) => !t.gameType || t.gameType === gameType)
  )
}

export default TEMPLATES
